import qs from 'qs'
import { MPMEnvType } from 'utils/env'
import { useAnalytics } from 'utils/analytics'
import { FireflyEvent } from '../utils/fireflyEvent'
import { safeTgtLogger, NO_STACK_TRACE } from '../utils/safeTgtLogger'
import { getFriendlyApiName } from '../utils/apiNameMap'
import { TIME } from 'utils/misc'
import { getDeviceSummary } from '../utils/helpers'
import { HttpStatus } from '../constants/constants'

export type MethodType = 'GET' | 'POST' | 'PUT' | 'DELETE'

export class ApiError extends Error {
    constructor(
        public readonly status: number,
        message: string,
    ) {
        super(message)
        this.name = 'ApiError'
    }
}

export type FetchProps<TBody> = {
    env: MPMEnvType
    accessToken: string
    location?: string
    dynamicSubPath?: string
    current_date?: string
    prepend_footer_to_rows?: boolean
    filters?:
        | {
              [key: string]: string
          }
        | [
              {
                  [key: string]: string
              },
          ]
    body?: TBody
    method?: MethodType
    trackEvent?: ReturnType<typeof useAnalytics>['trackEvent']
}

const timeout = TIME.twentySeconds

export const fetchData =
    <TResponse, TBody = null>(
        rootPath: string,
        shouldGetAudioBlobAsUrl: boolean = false,
    ) =>
    async ({
        env,
        accessToken,
        location,
        dynamicSubPath,
        current_date,
        filters,
        method = 'GET',
        body,
        trackEvent,
        prepend_footer_to_rows = false,
    }: FetchProps<TBody>): Promise<TResponse> => {
        let startTime: number, endTime: number
        const { apiKey, baseUrl } = env
        let urlPath = `${baseUrl}${rootPath}`

        if (dynamicSubPath) {
            urlPath += dynamicSubPath
        }

        let url = urlPath
        const parameters = {
            key: apiKey,
            ...filters,
        } as { [key: string]: string }
        if (current_date) {
            parameters.current_date = current_date
        } else if (location) {
            parameters.location = location
        }
        if (prepend_footer_to_rows) {
            parameters.prepend_footer_to_rows =
                prepend_footer_to_rows.toString()
        }
        const queryParams = qs.stringify(parameters)
        url += `?${queryParams}`

        const options = {
            method,
            headers: {
                Accept: shouldGetAudioBlobAsUrl
                    ? 'audio/mpeg'
                    : 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
                'x-api-key': apiKey,
            },
            body: body ? JSON.stringify(body) : null,
        }
        let response: Response | undefined
        startTime = new Date().getTime()
        const filtersAsString = qs.stringify(filters)
        try {
            response = await fetch(url, {
                ...options,
                signal: AbortSignal.timeout(timeout),
            })
        } catch (error) {
            safeTgtLogger.error('API call failed - timeout or network error', {
                error: {
                    message:
                        error instanceof Error ? error.message : String(error),
                    type: error instanceof Error ? error.name : 'Error',
                    stack_trace:
                        error instanceof Error && error.stack
                            ? error.stack
                            : 'No stack trace available',
                },
                event: {
                    action: `api_call_failed:${getFriendlyApiName(url)}`,
                    category: 'network',
                    outcome: 'failure',
                },
                url: {
                    path: urlPath,
                    query: filtersAsString,
                },
                http: {
                    request: {
                        method,
                    },
                },
            })
            endTime = new Date().getTime()
            if (trackEvent) {
                trackEvent!(
                    createFireflyEvent(
                        url,
                        (endTime - startTime) / 1000,
                        response,
                        error instanceof Error
                            ? error.message
                            : 'Request failed',
                    ),
                    {
                        client: {
                            page_url: url,
                        },
                    },
                )
            }
            throw error
        }

        if (!response.ok) {
            const responseErrorText = await response.text()
            let responseErrorDescription = responseErrorText

            // Try to parse JSON and extract just the message
            try {
                const errorJson = JSON.parse(responseErrorText)
                responseErrorDescription =
                    errorJson.message || responseErrorText
            } catch {
                // If JSON parsing fails, use the full text
                responseErrorDescription = responseErrorText
            }

            endTime = new Date().getTime()
            if (trackEvent) {
                trackEvent!(
                    createFireflyEvent(
                        url,
                        (endTime - startTime) / 1000,
                        response,
                        responseErrorDescription,
                    ),
                    {
                        client: {
                            page_url: url,
                        },
                    },
                )
            }
            safeTgtLogger.error('API call returned error status', {
                error: {
                    message: responseErrorDescription,
                    code: response?.status.toString(),
                    stack_trace: NO_STACK_TRACE,
                },
                event: {
                    action: `api_call_error_response:${getFriendlyApiName(url)}`,
                    category: 'network',
                    outcome: 'failure',
                },
                url: {
                    path: urlPath,
                    query: filtersAsString,
                },
                http: {
                    request: {
                        method,
                    },
                    response: {
                        status_code: response?.status,
                    },
                },
            })
            throw new ApiError(
                response.status,
                `Error Status Code - ${response.status.toString()} | Error Description - ${responseErrorDescription}`,
            )
        }

        const contentType = response.headers.get('content-type')
        if (
            response.status === HttpStatus.NO_CONTENT ||
            (contentType &&
                !(
                    contentType.includes('application/json') ||
                    contentType.includes('audio/mpeg')
                ))
        ) {
            const errorMessage = `API call returned no content or non-JSON content type.
            Status: ${response.status}, Content-Type: ${contentType}`
            endTime = new Date().getTime()
            if (trackEvent) {
                trackEvent!(
                    createFireflyEvent(
                        url,
                        (endTime - startTime) / 1000,
                        response,
                        errorMessage,
                    ),
                    {
                        client: {
                            page_url: url,
                        },
                    },
                )
            }
            safeTgtLogger.error(
                'API call returned no content or non-JSON content type',
                {
                    error: {
                        message: errorMessage,
                        code: response?.status.toString(),
                        stack_trace: NO_STACK_TRACE,
                    },
                    event: {
                        action: `api_call_empty_response:${getFriendlyApiName(url)}`,
                        category: 'error_handling',
                        outcome: 'failure',
                    },
                    url: {
                        path: urlPath,
                        query: filtersAsString,
                    },
                    http: {
                        request: {
                            method,
                        },
                        response: {
                            status_code: response?.status,
                        },
                    },
                },
            )
        }

        const parsedResponse = shouldGetAudioBlobAsUrl
            ? await response.blob()
            : await response.json()
        const formattedResponse = shouldGetAudioBlobAsUrl
            ? URL.createObjectURL(parsedResponse)
            : parsedResponse
        endTime = new Date().getTime()
        if (trackEvent) {
            trackEvent!(
                createFireflyEvent(url, (endTime - startTime) / 1000, response),
                {
                    client: {
                        page_url: url,
                    },
                },
            )
        }
        safeTgtLogger.info('API call success', {
            event: {
                action: `api_call_success:${getFriendlyApiName(url)}`,
                category: 'network',
                outcome: 'success',
            },
            url: {
                path: urlPath,
                query: filtersAsString,
            },
            http: {
                request: {
                    method,
                },
                response: {
                    status_code: response?.status,
                },
            },
        })
        return formattedResponse
    }

export const fetchGeneralData =
    <T, TBody = null>(rootPath: string) =>
    async ({
        env,
        accessToken,
        location,
        dynamicSubPath,
        current_date,
        filters,
        method = 'GET',
        body,
        trackEvent,
        prepend_footer_to_rows = false,
    }: FetchProps<TBody>): Promise<T> => {
        return fetchData<T, TBody>(
            rootPath,
            false,
        )({
            env,
            accessToken,
            location,
            dynamicSubPath,
            current_date,
            filters,
            method,
            body,
            trackEvent,
            prepend_footer_to_rows,
        })
    }

export function createFireflyEvent(
    url: string,
    responseTime: number,
    response?: Response,
    errorMessage?: string,
): FireflyEvent {
    let errorObj = undefined
    if (errorMessage) {
        // Use response status as error code, or fall back to the error message itself
        let errorCode = ''
        if (response?.status) {
            errorCode = String(response.status)
        } else {
            errorCode = errorMessage
        }

        errorObj = {
            id: errorCode,
            message: errorMessage ? errorMessage : '',
        }
    }
    const deviceSummary = getDeviceSummary()
    return {
        application: {
            version: process.env.APP_VERSION || '0.0.0',
            name: 'MPM',
        },
        event: {
            type: 'SERVICE_CALL',
        },
        error: errorObj,
        view: {
            additionalInfo: {
                request_url: url,
                device_type: deviceSummary.device,
            },
        },
    }
}
